import mammoth from "mammoth/mammoth.browser";
import * as pdfjs from "pdfjs-dist/build/pdf";
import { esc, defaultSettings, PAGE_SIZES } from "./pages";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

export async function importDocx(file) {
  const r = await mammoth.convertToHtml({ arrayBuffer: await file.arrayBuffer() });
  return r.value || "<p><br></p>";
}

function pageToHtml(items) {
  const lines = [];
  for (const it of items) {
    if (!("str" in it) || !it.str) continue;
    const y = it.transform[5];
    const h = Math.abs(it.transform[3]) || it.height || 10;
    let ln = lines.find((l) => Math.abs(l.y - y) <= h * 0.4);
    if (!ln) { ln = { y, h, items: [] }; lines.push(ln); }
    ln.items.push({ x: it.transform[4], str: it.str, w: it.width });
    ln.h = Math.max(ln.h, h);
  }
  lines.sort((a, b) => b.y - a.y);
  const sizes = lines.map((l) => l.h).sort((a, b) => a - b);
  const med = sizes[Math.floor(sizes.length / 2)] || 10;
  lines.forEach((l) => { l.x0 = Math.min(...l.items.map((i) => i.x)); l.x1 = Math.max(...l.items.map((i) => i.x + i.w)); });
  const maxW = Math.max(...lines.map((l) => l.x1 - l.x0), 1);
  const paras = [];
  let cur = null;
  let prev = null;
  for (const l of lines) {
    l.items.sort((a, b) => a.x - b.x);
    let text = "";
    let end = null;
    for (const it of l.items) {
      if (end !== null && it.x - end > l.h * 0.15 && !text.endsWith(" ") && !it.str.startsWith(" ")) text += " ";
      text += it.str;
      end = it.x + it.w;
    }
    text = text.trim();
    if (!text) continue;
    const heading = l.h > med * 1.3;
    const gap = prev ? prev.y - l.y : 0;
    if (!cur || heading || cur.heading || gap > Math.max(prev.h, l.h) * 1.75) {
      cur = { heading, text };
      paras.push(cur);
    } else {
      const short = prev.x1 - prev.x0 < maxW * 0.7;
      cur.text = short ? `${cur.text}\n${text}` : cur.text.endsWith("-") ? cur.text.slice(0, -1) + text : `${cur.text} ${text}`;
    }
    prev = l;
  }
  return paras.map((p) => (p.heading ? `<h2>${esc(p.text)}</h2>` : `<p>${esc(p.text)}</p>`)).join("");
}

export async function importPdf(file) {
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const parts = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const tc = await page.getTextContent();
    parts.push(pageToHtml(tc.items));
  }
  const html = parts.filter(Boolean).join('<div class="page-break" contenteditable="false"></div>');
  if (!html) throw new Error("No selectable text found in this PDF (it may be a scanned image).");
  return html;
}

export async function importPdfPages(file) {
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const v1 = (await pdf.getPage(1)).getViewport({ scale: 1 });
  const land = v1.width > v1.height;
  const ratio = Math.max(v1.width, v1.height) / Math.min(v1.width, v1.height);
  const size = Math.abs(ratio - 1.4142) < Math.abs(ratio - 1.2941) ? "A4" : "Letter";
  const p = PAGE_SIZES[size];
  const ar = land ? `${p.h} / ${p.w}` : `${p.w} / ${p.h}`;
  const settings = { ...defaultSettings(), size, orientation: land ? "landscape" : "portrait", margins: { top: 0, right: 0, bottom: 0, left: 0 } };
  const parts = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const vp = page.getViewport({ scale: 2 });
    const c = document.createElement("canvas");
    c.width = vp.width;
    c.height = vp.height;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    parts.push(`<div class="pdf-page" contenteditable="false" data-page="${i}" style="aspect-ratio:${ar}"><img class="pdf-page-img" src="${c.toDataURL("image/jpeg", 0.85)}" alt="Page ${i}"></div>`);
  }
  return { html: parts.join(""), settings };
}
